/**
 * Apps2Go Hub – Kokoushuone (WebRTC P2P mesh).
 *
 * Polut:
 *   /meeting           → MeetingDashboard (auth) – uudet kokoukset, listaus, liittyminen
 *   /meet/:code        → MeetingRoom (ei auth) – WebRTC video/audio/näytönjako
 *
 * Tekninen pohja:
 *   - WebSocket signaling (FastAPI /api/meetings/ws/{code})
 *   - RTCPeerConnection mesh: jokainen osallistuja luo peer-yhteyden kaikkiin muihin
 *   - getUserMedia (cam+mic), getDisplayMedia (näytönjako track-replace)
 *   - Chat-viestit broadcasted signalingin kautta
 *   - Free-version: ei TURN-serveriä, vain STUN (public Google)
 */
import React, { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Mic, MicOff, Video, VideoOff, MonitorUp, PhoneOff, MessageCircle,
  Copy, Users, Send, X, Loader2, ScreenShare, ScreenShareOff,
} from "lucide-react";
import { toast } from "sonner";
import { copyToClipboard } from "@/lib/browser";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const WS_URL = BACKEND_URL.replace(/^http/, "ws") + "/api/meetings/ws";

const ICE_SERVERS = {
  iceServers: [
    { urls: "stun:stun.l.google.com:19302" },
    { urls: "stun:stun1.l.google.com:19302" },
    { urls: "stun:stun2.l.google.com:19302" },
  ],
};

// ===========================================================================
// MeetingRoom – varsinainen videohuone
// ===========================================================================
export default function MeetingRoom() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialName = searchParams.get("name") || "";

  const [name, setName] = useState(initialName);
  const [nameAsked, setNameAsked] = useState(!initialName);
  const [roomInfo, setRoomInfo] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);

  // Connection state
  const [connected, setConnected] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [disconnected, setDisconnected] = useState(false);  // pudotuksen havainnut tila
  const wasConnectedRef = useRef(false);
  const inviteToken = searchParams.get("inv");              // /meet/:code?inv=…
  const wsRef = useRef(null);
  const myPeerIdRef = useRef(null);
  const peersRef = useRef({});           // peer_id → { pc, name, video, audio, screen, stream }
  const [peers, setPeers] = useState({}); // re-render trigger

  // Media
  const localStreamRef = useRef(null);
  const screenStreamRef = useRef(null);
  const localVideoRef = useRef(null);
  const [micOn, setMicOn] = useState(true);
  const [camOn, setCamOn] = useState(true);
  const [sharingScreen, setSharingScreen] = useState(false);

  // Chat
  const [chatOpen, setChatOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");

  // -----------------------------------------------------------------------
  // Huoneen perustiedot
  // -----------------------------------------------------------------------
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${BACKEND_URL}/api/meetings/by-code/${code}`);
        if (!r.ok) {
          setErrorMsg("Kokousta ei löydy tai linkki on virheellinen.");
          return;
        }
        setRoomInfo(await r.json());
      } catch {
        setErrorMsg("Yhteysvirhe.");
      }
    })();
  }, [code]);

  // -----------------------------------------------------------------------
  // Liittymiskutsut – peer-yhteyden luonti yhteen olemassa olevaan peeriin
  // -----------------------------------------------------------------------
  const createPeerConnection = useCallback((peerId, peerName, isInitiator) => {
    const pc = new RTCPeerConnection(ICE_SERVERS);

    // Lisää omat track:t pc:hen
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach((t) => pc.addTrack(t, localStreamRef.current));
    }

    pc.onicecandidate = (ev) => {
      if (ev.candidate && wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          type: "signal", target: peerId,
          signal: { type: "candidate", candidate: ev.candidate },
        }));
      }
    };

    pc.ontrack = (ev) => {
      const remoteStream = ev.streams[0];
      peersRef.current[peerId] = { ...(peersRef.current[peerId] || {}), stream: remoteStream };
      setPeers({ ...peersRef.current });
    };

    pc.onconnectionstatechange = () => {
      if (pc.connectionState === "failed" || pc.connectionState === "closed") {
        // Pyhi kanava
        delete peersRef.current[peerId];
        setPeers({ ...peersRef.current });
      }
    };

    peersRef.current[peerId] = {
      pc, name: peerName, video: true, audio: true, screen: false,
      stream: peersRef.current[peerId]?.stream,
    };
    setPeers({ ...peersRef.current });
    return pc;
  }, []);

  const initiateOffer = useCallback(async (peerId) => {
    const peer = peersRef.current[peerId];
    if (!peer) return;
    try {
      const offer = await peer.pc.createOffer();
      await peer.pc.setLocalDescription(offer);
      wsRef.current?.send(JSON.stringify({
        type: "signal", target: peerId,
        signal: { type: "offer", sdp: offer },
      }));
    } catch (e) {
      console.error("Offer error", e);
    }
  }, []);

  const handleSignal = useCallback(async (from, signal) => {
    let peer = peersRef.current[from];
    if (!peer) {
      // Uusi peer aloittaa offerilla — luo PC vastaanottavana
      const pc = createPeerConnection(from, "Vieras", false);
      peer = peersRef.current[from];
    }
    try {
      if (signal.type === "offer") {
        await peer.pc.setRemoteDescription(new RTCSessionDescription(signal.sdp));
        const answer = await peer.pc.createAnswer();
        await peer.pc.setLocalDescription(answer);
        wsRef.current?.send(JSON.stringify({
          type: "signal", target: from,
          signal: { type: "answer", sdp: answer },
        }));
      } else if (signal.type === "answer") {
        await peer.pc.setRemoteDescription(new RTCSessionDescription(signal.sdp));
      } else if (signal.type === "candidate" && signal.candidate) {
        try {
          await peer.pc.addIceCandidate(new RTCIceCandidate(signal.candidate));
        } catch (e) {
          console.warn("ICE candidate add failed", e);
        }
      }
    } catch (e) {
      console.error("handleSignal error", e);
    }
  }, [createPeerConnection]);

  // -----------------------------------------------------------------------
  // Pää-join
  // -----------------------------------------------------------------------
  const joinRoom = useCallback(async () => {
    setConnecting(true);
    setNameAsked(false);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: 1280, height: 720 },
        audio: true,
      });
      localStreamRef.current = stream;
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
      }
    } catch (e) {
      // Yritä ilman videota
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        localStreamRef.current = stream;
        setCamOn(false);
      } catch {
        toast.error("Mikrofonin/kameran käyttö estetty. Tarkista selaimen oikeudet.");
        setConnecting(false);
        setNameAsked(true);
        return;
      }
    }

    const ws = new WebSocket(`${WS_URL}/${code}`);
    wsRef.current = ws;
    ws.onopen = () => {
      ws.send(JSON.stringify({ type: "join", name: name || "Vieras" }));
    };
    ws.onmessage = async (ev) => {
      let msg;
      try { msg = JSON.parse(ev.data); } catch { return; }
      if (msg.type === "joined") {
        myPeerIdRef.current = msg.peer_id;
        setConnected(true);
        setConnecting(false);
        // Luo offer kaikkiin olemassa olevin peer:eihin
        for (const p of msg.peers) {
          createPeerConnection(p.peer_id, p.name, true);
          await initiateOffer(p.peer_id);
        }
      } else if (msg.type === "peer-joined") {
        // Uusi peer liittyi — odotetaan että lähettää offerin meille
        // Voimme luoda placeholder peeritiedon nimellä
        peersRef.current[msg.peer_id] = {
          ...(peersRef.current[msg.peer_id] || {}),
          name: msg.name, video: msg.video, audio: msg.audio, screen: msg.screen,
        };
        setPeers({ ...peersRef.current });
      } else if (msg.type === "signal") {
        await handleSignal(msg.from, msg.signal);
      } else if (msg.type === "peer-left") {
        const peer = peersRef.current[msg.peer_id];
        if (peer) {
          try { peer.pc?.close(); } catch (_e) { /* ignore */ }
          delete peersRef.current[msg.peer_id];
          setPeers({ ...peersRef.current });
        }
      } else if (msg.type === "chat") {
        setMessages((m) => [...m, msg]);
      } else if (msg.type === "media") {
        const p = peersRef.current[msg.peer_id];
        if (p) {
          if (msg.video !== undefined) p.video = msg.video;
          if (msg.audio !== undefined) p.audio = msg.audio;
          if (msg.screen !== undefined) p.screen = msg.screen;
          setPeers({ ...peersRef.current });
        }
      } else if (msg.type === "error") {
        toast.error(msg.detail);
        setErrorMsg(msg.detail);
      }
    };
    ws.onerror = (e) => {
      console.error("WS error", e);
    };
    ws.onclose = () => {
      setConnected(false);
      // Pudotus: jos olimme yhdistettynä → näytä rejoin-banneri
      if (wasConnectedRef.current) {
        setDisconnected(true);
      }
    };
  }, [code, name, createPeerConnection, initiateOffer, handleSignal]);

  // Pidetään muistissa että olemme olleet yhdistettynä
  useEffect(() => {
    if (connected) {
      wasConnectedRef.current = true;
      setDisconnected(false);
    }
  }, [connected]);

  // Rejoin-flow: pyydä uusi room_code käyttäen invite-tokenin session_id:tä,
  // sitten kutsu joinRoom uudelleen samalla nimellä.
  const reconnectAfterDrop = useCallback(async () => {
    setDisconnected(false);
    if (inviteToken) {
      try {
        const sid = localStorage.getItem(`meeting_invite_session_${inviteToken}`);
        await fetch(`${BACKEND_URL}/api/meetings/invite/${inviteToken}/use`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ name, session_id: sid || undefined }),
        });
      } catch (e) {
        console.warn("Invite rejoin sync failed", e);
      }
    }
    // Re-join: peer-yhteydet aukaistaan uudestaan (vanha localStream voidaan käyttää uudelleen)
    Object.values(peersRef.current).forEach((p) => { try { p.pc?.close(); } catch (_e) { /* ignore */ } });
    peersRef.current = {};
    setPeers({});
    joinRoom();
  }, [inviteToken, name, joinRoom]);

  // -----------------------------------------------------------------------
  // Media-toggle
  // -----------------------------------------------------------------------
  const toggleMic = () => {
    const stream = localStreamRef.current;
    if (!stream) return;
    stream.getAudioTracks().forEach((t) => { t.enabled = !t.enabled; });
    const enabled = stream.getAudioTracks()[0]?.enabled ?? false;
    setMicOn(enabled);
    wsRef.current?.send(JSON.stringify({ type: "media", audio: enabled, video: camOn, screen: sharingScreen }));
  };

  const toggleCam = () => {
    const stream = localStreamRef.current;
    if (!stream) return;
    stream.getVideoTracks().forEach((t) => { t.enabled = !t.enabled; });
    const enabled = stream.getVideoTracks()[0]?.enabled ?? false;
    setCamOn(enabled);
    wsRef.current?.send(JSON.stringify({ type: "media", audio: micOn, video: enabled, screen: sharingScreen }));
  };

  const toggleScreen = async () => {
    if (sharingScreen) {
      // Stop screen share, revert to camera
      try {
        screenStreamRef.current?.getTracks().forEach((t) => t.stop());
      } catch (_e) { /* ignore */ }
      screenStreamRef.current = null;
      const camTrack = localStreamRef.current?.getVideoTracks()[0];
      if (camTrack) {
        replaceTrackOnAllPeers(camTrack);
        // Palauta paikallinen preview kameraan
        if (localVideoRef.current && localStreamRef.current) {
          localVideoRef.current.srcObject = localStreamRef.current;
        }
      }
      setSharingScreen(false);
      wsRef.current?.send(JSON.stringify({ type: "media", audio: micOn, video: camOn, screen: false }));
      return;
    }
    if (!navigator.mediaDevices?.getDisplayMedia) {
      toast.error("Tämä selain ei tue näytönjakoa. Käytä Chromea, Edgeä tai Firefoxia.");
      return;
    }
    try {
      const screen = await navigator.mediaDevices.getDisplayMedia({
        video: { cursor: "always", frameRate: { ideal: 30, max: 60 } },
        audio: true,   // yritetään jakaa myös järjestelmä-audio (Chrome tukee tab-shareissa)
      });
      screenStreamRef.current = screen;
      const videoTrack = screen.getVideoTracks()[0];
      if (!videoTrack) throw new Error("Näytön videotrack puuttuu");

      // Kun käyttäjä lopettaa näytönjaon selaimen omasta "Lopeta jako" -napista
      videoTrack.onended = () => {
        if (screenStreamRef.current) {
          // varmistetaan, että toggle juoksee vain kerran
          screenStreamRef.current = null;
          setSharingScreen(false);
          const camTrack = localStreamRef.current?.getVideoTracks()[0];
          if (camTrack) replaceTrackOnAllPeers(camTrack);
          if (localVideoRef.current && localStreamRef.current) {
            localVideoRef.current.srcObject = localStreamRef.current;
          }
          wsRef.current?.send(JSON.stringify({ type: "media", audio: micOn, video: camOn, screen: false }));
        }
      };

      replaceTrackOnAllPeers(videoTrack);
      // Näytön audio (jos käyttäjä valitsi "Jaa myös audio") → lisää sender peerille
      const screenAudioTrack = screen.getAudioTracks()[0];
      if (screenAudioTrack) {
        Object.values(peersRef.current).forEach((p) => {
          try { p.pc?.addTrack(screenAudioTrack, screen); } catch (_e) { /* ignore */ }
        });
      }
      // Show locally
      if (localVideoRef.current) {
        const combined = new MediaStream([
          videoTrack,
          ...(localStreamRef.current?.getAudioTracks() || []),
        ]);
        localVideoRef.current.srcObject = combined;
      }
      setSharingScreen(true);
      wsRef.current?.send(JSON.stringify({ type: "media", audio: micOn, video: camOn, screen: true }));
      toast.success("Näytönjako alkoi");
    } catch (e) {
      if (e?.name === "NotAllowedError") {
        toast.error("Näytönjako peruttu");
      } else {
        toast.error("Näytönjako epäonnistui: " + (e?.message || "tuntematon virhe"));
      }
    }
  };

  const replaceTrackOnAllPeers = (newTrack) => {
    Object.values(peersRef.current).forEach((p) => {
      const sender = p.pc?.getSenders().find((s) => s.track?.kind === "video");
      if (sender) {
        sender.replaceTrack(newTrack).catch(() => { /* ignore */ });
      }
    });
  };

  // -----------------------------------------------------------------------
  // Lähtö
  // -----------------------------------------------------------------------
  const leave = () => {
    try {
      wsRef.current?.close();
    } catch (_e) { /* ignore */ }
    Object.values(peersRef.current).forEach((p) => {
      try { p.pc?.close(); } catch (_e) { /* ignore */ }
    });
    peersRef.current = {};
    localStreamRef.current?.getTracks().forEach((t) => t.stop());
    screenStreamRef.current?.getTracks().forEach((t) => t.stop());
    navigate("/meeting");
  };

  useEffect(() => {
    return () => {
      // cleanup on unmount
      try { wsRef.current?.close(); } catch (_e) { /* ignore */ }
      Object.values(peersRef.current).forEach((p) => { try { p.pc?.close(); } catch (_e) { /* ignore */ } });
      localStreamRef.current?.getTracks().forEach((t) => t.stop());
      screenStreamRef.current?.getTracks().forEach((t) => t.stop());
    };
  }, []);

  const sendChat = () => {
    if (!chatInput.trim()) return;
    wsRef.current?.send(JSON.stringify({ type: "chat", text: chatInput }));
    setChatInput("");
  };

  const copyJoinLink = async () => {
    const url = `${window.location.origin}/meet/${code}`;
    const ok = await copyToClipboard(url);
    toast[ok ? "success" : "info"](ok ? "Linkki kopioitu" : `Linkki: ${url}`);
  };

  // -----------------------------------------------------------------------
  // Rendering
  // -----------------------------------------------------------------------
  if (errorMsg) {
    return (
      <div className="min-h-screen app-bg flex items-center justify-center px-4">
        <div className="glass-card p-8 text-center max-w-md">
          <X className="w-12 h-12 mx-auto text-red-400 mb-3" />
          <h1 className="text-xl text-white">Virhe</h1>
          <p className="text-white/60 text-sm mt-2">{errorMsg}</p>
          <Button onClick={() => navigate("/")} className="mt-4 btn-luxe">Etusivulle</Button>
        </div>
      </div>
    );
  }

  if (nameAsked) {
    return (
      <div className="min-h-screen app-bg flex items-center justify-center px-4">
        <div className="glass-card p-8 max-w-md w-full space-y-4">
          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] text-amber-300">Kokous</p>
            <h1 className="text-2xl font-display gradient-text-luxe">
              {roomInfo?.title || "Liity kokoukseen"}
            </h1>
            {roomInfo?.owner_name && (
              <p className="text-white/50 text-sm mt-1">Järjestäjä: {roomInfo.owner_name}</p>
            )}
            <p className="text-white/40 text-xs mt-1">Huoneen koodi: {code}</p>
          </div>
          <div>
            <label className="text-xs text-white/60">Nimesi</label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Etu- ja sukunimi"
              className="mt-1"
              data-testid="meet-name-input"
            />
          </div>
          <Button
            onClick={joinRoom}
            disabled={!name.trim() || connecting}
            className="btn-luxe w-full"
            data-testid="meet-join-button"
          >
            {connecting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
            Liity kokoukseen
          </Button>
          <p className="text-[10px] text-white/40 text-center">
            Selain kysyy lupaa käyttää kameraa ja mikrofonia.
          </p>
        </div>
      </div>
    );
  }

  const peerList = Object.entries(peers);
  const tileCount = peerList.length + 1;
  const cols = tileCount <= 1 ? "grid-cols-1" :
               tileCount <= 2 ? "grid-cols-1 md:grid-cols-2" :
               tileCount <= 4 ? "grid-cols-2" :
               "grid-cols-2 md:grid-cols-3";

  return (
    <div className="min-h-screen bg-zinc-950 flex flex-col" data-testid="meeting-room">
      {/* Disconnected banner – pudotuksen jälkeen */}
      {disconnected && (
        <div className="bg-amber-500/15 border-b border-amber-500/30 px-4 py-2 flex items-center justify-between gap-3" data-testid="disconnect-banner">
          <div className="text-sm">
            <span className="text-amber-200 font-medium">Yhteys katkesi.</span>
            <span className="text-white/70 ml-2 text-xs">Voit yhdistää samaan kokoukseen uudelleen.</span>
          </div>
          <Button
            onClick={reconnectAfterDrop}
            size="sm"
            className="btn-luxe inline-flex items-center gap-1"
            data-testid="reconnect-button"
          >
            <Loader2 className="w-3 h-3" /> Yhdistä uudelleen
          </Button>
        </div>
      )}

      {/* Top bar */}
      <header className="flex items-center justify-between px-4 py-3 border-b border-white/5">
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-amber-300">Kokoushuone</p>
          <h1 className="text-base text-white font-display">{roomInfo?.title || code}</h1>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={copyJoinLink} className="inline-flex items-center gap-2" data-testid="meet-copy-link">
            <Copy className="w-3 h-3" /> Kopioi linkki
          </Button>
          <div className="inline-flex items-center gap-1.5 text-xs text-white/60 px-2 py-1 bg-white/5 rounded-full">
            <Users className="w-3 h-3" /> {tileCount}
          </div>
        </div>
      </header>

      {/* Video grid */}
      <main className="flex-1 p-4 overflow-hidden">
        <div className={`grid ${cols} gap-3 h-full`}>
          {/* Self tile */}
          <VideoTile
            stream={localStreamRef.current}
            video={camOn || sharingScreen}
            audio={micOn}
            screen={sharingScreen}
            name={`${name} (sinä)`}
            self
            videoRef={localVideoRef}
          />
          {peerList.map(([pid, p]) => (
            <VideoTile
              key={pid}
              stream={p.stream}
              video={p.video !== false}
              audio={p.audio !== false}
              screen={p.screen}
              name={p.name || "Vieras"}
            />
          ))}
        </div>
      </main>

      {/* Bottom toolbar */}
      <footer className="px-4 py-3 border-t border-white/5 flex items-center justify-center gap-3">
        <CtrlBtn active={micOn} onClick={toggleMic} testid="meet-toggle-mic"
                 onIcon={<Mic className="w-5 h-5" />} offIcon={<MicOff className="w-5 h-5" />}
                 label={micOn ? "Mykistä" : "Avaa mikki"} />
        <CtrlBtn active={camOn} onClick={toggleCam} testid="meet-toggle-cam"
                 onIcon={<Video className="w-5 h-5" />} offIcon={<VideoOff className="w-5 h-5" />}
                 label={camOn ? "Sulje kamera" : "Avaa kamera"} />
        <CtrlBtn active={sharingScreen} onClick={toggleScreen} testid="meet-toggle-screen"
                 onIcon={<ScreenShare className="w-5 h-5" />} offIcon={<ScreenShareOff className="w-5 h-5" />}
                 toggledClass="bg-amber-500 text-zinc-900"
                 label={sharingScreen ? "Lopeta jako" : "Jaa näyttö"} />
        <CtrlBtn active={!chatOpen} onClick={() => setChatOpen((v) => !v)} testid="meet-toggle-chat"
                 onIcon={<MessageCircle className="w-5 h-5" />} offIcon={<MessageCircle className="w-5 h-5" />}
                 toggledClass={chatOpen ? "bg-purple-500 text-white" : ""}
                 label="Chat" />
        <Button
          onClick={leave}
          className="rounded-full px-4 py-3 bg-red-500 hover:bg-red-600 text-white inline-flex items-center gap-2"
          data-testid="meet-leave"
        >
          <PhoneOff className="w-4 h-4" /> Lähde
        </Button>
      </footer>

      {/* Chat sidebar */}
      {chatOpen && (
        <aside className="fixed right-0 top-0 bottom-0 w-80 bg-zinc-900 border-l border-white/10 flex flex-col z-50">
          <header className="px-4 py-3 border-b border-white/5 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-white">Chat</h2>
            <Button size="icon" variant="ghost" onClick={() => setChatOpen(false)} className="w-7 h-7">
              <X className="w-3 h-3" />
            </Button>
          </header>
          <div className="flex-1 overflow-y-auto p-3 space-y-2">
            {messages.length === 0 && <p className="text-white/30 text-xs text-center">Ei vielä viestejä.</p>}
            {messages.map((m, i) => (
              <div key={i} className="text-sm">
                <div className="flex items-baseline gap-2">
                  <span className="text-amber-300 font-semibold">{m.from_name}</span>
                  <span className="text-[10px] text-white/30">{m.ts?.slice(11, 16)}</span>
                </div>
                <p className="text-white/80">{m.text}</p>
              </div>
            ))}
          </div>
          <div className="p-3 border-t border-white/5 flex gap-2">
            <Input
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && sendChat()}
              placeholder="Viesti..."
              data-testid="meet-chat-input"
            />
            <Button onClick={sendChat} className="btn-luxe" data-testid="meet-chat-send">
              <Send className="w-3 h-3" />
            </Button>
          </div>
        </aside>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// VideoTile – yksittäisen osallistujan ruutu
// ---------------------------------------------------------------------------
function VideoTile({ stream, video, audio, screen, name, self = false, videoRef }) {
  const myRef = useRef(null);
  const ref = videoRef || myRef;

  useEffect(() => {
    if (ref.current && stream && ref.current.srcObject !== stream) {
      ref.current.srcObject = stream;
    }
  }, [stream, ref]);

  return (
    <div className="relative rounded-2xl overflow-hidden bg-zinc-900 border border-white/5" data-testid={`meet-tile-${self ? "self" : "remote"}`}>
      {/* Video aina renderöity jos streamissä on jotain – tämä mahdollistaa myös audion toistumisen
          vaikka kamera olisi pois (audio-track on samassa MediaStreamissä). */}
      {stream && (
        <video
          ref={ref}
          autoPlay
          playsInline
          muted={self}
          className={`w-full h-full object-cover bg-black ${video ? "" : "opacity-0 absolute inset-0 pointer-events-none"}`}
          style={video ? null : { width: 1, height: 1 }}
          onLoadedMetadata={() => { try { ref.current?.play?.(); } catch (_e) { /* ignore */ } }}
        />
      )}
      {!video && (
        <div className="absolute inset-0 flex items-center justify-center bg-zinc-900">
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-amber-400 to-purple-600 flex items-center justify-center text-2xl font-bold text-white">
            {(name || "?").substring(0, 1).toUpperCase()}
          </div>
        </div>
      )}
      {/* Bottom info bar */}
      <div className="absolute bottom-0 left-0 right-0 px-3 py-2 bg-gradient-to-t from-black/80 to-transparent flex items-center justify-between text-xs">
        <span className="text-white">{name}{screen && " · jakaa näyttöä"}</span>
        <div className="flex items-center gap-1">
          {!audio && <MicOff className="w-3 h-3 text-red-400" />}
          {!video && <VideoOff className="w-3 h-3 text-red-400" />}
        </div>
      </div>
    </div>
  );
}

function CtrlBtn({ active, onClick, onIcon, offIcon, label, toggledClass, testid }) {
  return (
    <button
      onClick={onClick}
      title={label}
      data-testid={testid}
      className={`relative w-12 h-12 rounded-full flex items-center justify-center transition ${
        active
          ? (toggledClass || "bg-white/10 hover:bg-white/15 text-white")
          : "bg-red-500/80 hover:bg-red-500 text-white"
      }`}
    >
      {active ? onIcon : offIcon}
    </button>
  );
}
